import React, { useState, useRef, useEffect } from 'react';
import { Footprints, Trophy, RotateCcw, Flag } from 'lucide-react';

const GOAL = 40;      // taps to finish
const DURATION = 8500; // pace bot finish time (ms)

export default function StepRace() {
  const [phase, setPhase] = useState('idle'); // idle | racing | done
  const [you, setYou] = useState(0);
  const [bot, setBot] = useState(0);
  const [result, setResult] = useState(null);
  const [best, setBest] = useState(() => { try { return Number(localStorage.getItem('spc_steprace_best')) || 0; } catch { return 0; } });

  const youRef = useRef(0);
  const startRef = useRef(0);
  const rafRef = useRef(null);
  const phaseRef = useRef('idle');

  const setPhaseBoth = (p) => { phaseRef.current = p; setPhase(p); };
  const stop = () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); rafRef.current = null; };
  useEffect(() => () => stop(), []);

  const finishRace = (res, elapsed) => {
    stop();
    setPhaseBoth('done');
    setResult(res);
    if (res === 'win') {
      const t = Math.round(elapsed);
      setBest(prev => {
        if (!prev || t < prev) { try { localStorage.setItem('spc_steprace_best', String(t)); } catch {} return t; }
        return prev;
      });
    }
  };

  const loop = () => {
    if (phaseRef.current !== 'racing') return;
    const elapsed = performance.now() - startRef.current;
    const botP = Math.min(GOAL, (elapsed / DURATION) * GOAL);
    setBot(botP);
    if (youRef.current >= GOAL) { finishRace('win', elapsed); return; }
    if (botP >= GOAL) { finishRace('lose', elapsed); return; }
    rafRef.current = requestAnimationFrame(loop);
  };

  const begin = () => {
    youRef.current = 0; setYou(0); setBot(0); setResult(null);
    startRef.current = performance.now();
    setPhaseBoth('racing');
    rafRef.current = requestAnimationFrame(loop);
  };

  const tap = () => {
    if (phaseRef.current !== 'racing') return;
    youRef.current = Math.min(GOAL, youRef.current + 1);
    setYou(youRef.current);
    if (youRef.current >= GOAL) finishRace('win', performance.now() - startRef.current);
  };

  const youPct = Math.min(100, (you / GOAL) * 100);
  const botPct = Math.min(100, (bot / GOAL) * 100);

  return (
    <div className="solo-game" data-testid="solo-steprace">
      <div className="solo-head">
        <div className="solo-title"><Footprints size={18} /><span>Step Race</span></div>
        <div className="solo-score">
          {best > 0 && <span className="pill yellow"><Trophy size={12} /> Best {(best / 1000).toFixed(2)}s</span>}
        </div>
      </div>

      <div className="steprace-track">
        <div className="steprace-lane">
          <span className="steprace-label">You — {you}/{GOAL}</span>
          <div className="steprace-rail"><div className="steprace-fill you" style={{ width: `${youPct}%` }} data-testid="steprace-you-fill" /><Flag className="steprace-flag" size={16} /></div>
        </div>
        <div className="steprace-lane">
          <span className="steprace-label">Pace Bot</span>
          <div className="steprace-rail"><div className="steprace-fill bot" style={{ width: `${botPct}%` }} /><Flag className="steprace-flag" size={16} /></div>
        </div>
      </div>

      {phase === 'racing' ? (
        <button className="btn steprace-tap" onPointerDown={(e) => { e.preventDefault(); tap(); }} data-testid="steprace-tap">TAP! ({you}/{GOAL})</button>
      ) : (
        <button className="btn" onClick={begin} data-testid="steprace-start">
          {phase === 'idle' ? 'Start race' : <><RotateCcw size={15} /> Race again</>}
        </button>
      )}

      {phase === 'done' && (
        <div className={`solo-status ${result === 'win' ? 'win' : 'lose'}`} data-testid="steprace-result">
          {result === 'win' ? 'You beat the Pace Bot! 🏆' : 'Pace Bot got there first — go again!'}
        </div>
      )}
      {phase === 'idle' && <p className="steprace-hint">Tap as fast as you can to out-sprint the Pace Bot to the finish line.</p>}
    </div>
  );
}
